import { useState } from 'react';
import { Button, Flex, Tooltip, Typography } from 'antd';
import { LuChartColumnIncreasing, LuRotateCcw } from 'react-icons/lu';
import RangeSliderField from '@components/form/RangeSliderField';
import PropertyHistogram from './PropertyHistogram';

const { Text } = Typography;

/**
 * A single filter card rendered inside an antd Form.
 *
 * Renders a RangeSliderField (Form.Item) so the Form owns the field value.
 * Visual state (border, background) is derived from isActive and isDirty props
 * computed by the hook via onValuesChange.
 *
 * Disabled state (range collapsed by collection filter) renders a muted placeholder.
 *
 * @param {{
 *   filter: { id, label, unit, min, max, clamped?: boolean, disabled?: boolean },
 *   isActive: boolean,
 *   isDirty: boolean,
 *   showSlider?: boolean,
 *   histogramValues?: Array<number>,
 *   onClear: () => void,
 * }} props
 */
const FilterCard = ({ filter, isActive, isDirty, showSlider = true, histogramValues, onClear }) => {
	const {
		id,
		label,
		unit,
		min,
		max,
		conversionFactor,
		clamped = false,
		disabled = false,
	} = filter;
	const [showChart, setShowChart] = useState(false);
	const hasHistogram =
		!!histogramValues?.length && Number.isFinite(min) && Number.isFinite(max) && max > min;

	if (disabled) {
		return (
			<Flex
				vertical
				gap={8}
				style={{
					padding: '12px 16px',
					margin: '0 8px',
					borderRadius: 6,
					border: '1px solid transparent',
					opacity: 0.5,
				}}>
				<Text strong style={{ fontSize: '0.95rem' }}>
					{label}
				</Text>
				<Text type="secondary" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
					Excluded by collection filter
				</Text>
			</Flex>
		);
	}

	const cardBorder = (() => {
		if (isDirty) return '1px dashed var(--ant-color-warning)';
		if (isActive) return '1px solid var(--ant-color-primary)';
		return '1px solid transparent';
	})();

	const cardBackground = (() => {
		if (isDirty) return 'var(--ant-color-warning-bg)';
		if (isActive) return 'var(--ant-color-primary-bg)';
		return 'transparent';
	})();

	return (
		<Flex
			vertical
			gap={8}
			style={{
				padding: '12px 16px 20px',
				margin: '0 8px',
				borderRadius: 6,
				border: cardBorder,
				background: cardBackground,
			}}>
			{/* Header: label + unit (right-aligned) + clear button */}
			<Flex align="center" justify="space-between">
				<Flex align="baseline" gap={6} style={{ flex: 1, minWidth: 0 }}>
					<Text strong style={{ fontSize: '0.95rem' }}>
						{label}
					</Text>
					{unit && (
						<Text type="secondary" style={{ fontSize: '0.75rem' }}>
							· {unit}
						</Text>
					)}
				</Flex>
				{hasHistogram && (
					<Tooltip
						title={showChart ? 'Hide distribution chart' : 'Show distribution chart'}>
						<Button
							type={showChart ? 'default' : 'text'}
							size="small"
							onClick={() => setShowChart(prev => !prev)}
							aria-label={
								showChart
									? `Hide ${label} distribution chart`
									: `Show ${label} distribution chart`
							}
							aria-pressed={showChart}
							icon={<LuChartColumnIncreasing size={13} />}
							style={{ padding: '0 4px', height: 'auto', lineHeight: 1.6 }}
						/>
					</Tooltip>
				)}
				{(isActive || isDirty) && (
					<Button
						type="text"
						size="small"
						onClick={onClear}
						aria-label={`Reset ${label} filter`}
						icon={<LuRotateCcw size={13} />}
						style={{ padding: '0 4px', height: 'auto', lineHeight: 1 }}
					/>
				)}
			</Flex>

			{/* Clamped info */}
			{clamped && (
				<Text type="warning" style={{ fontSize: '0.75rem' }}>
					Adjusted to collection filter range
				</Text>
			)}

			{/* Distribution chart — toggled via the chart icon button above */}
			{showChart && hasHistogram && (
				<PropertyHistogram
					values={histogramValues}
					min={min}
					max={max}
					unit={unit}
					conversionFactor={conversionFactor}
				/>
			)}

			{/* Form-connected range slider — Form.Item name = filter id */}
			<RangeSliderField
				name={id}
				min={min}
				max={max}
				unit={unit}
				conversionFactor={conversionFactor}
				showSlider={showSlider}
			/>
		</Flex>
	);
};

export default FilterCard;
